import React from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Icon,
  IconButton,
  MenuItem,
  TextField
} from '@material-ui/core';
import { Formik, FieldArray } from 'formik';
import * as yup from 'yup';
import { calculateSsortOrder } from 'helpers/sortOrderCalculations';
import {
  discount,
  frequencyOptions,
  emptySubscription
} from './helpers';
import { useStyles } from './styles';


const validationSchema =  yup.object({
  subscriptions: yup
    .array().of(
      yup.object().shape({
        frequency: yup
          .string()
          .required('Field is required'),
        percent_discount: yup
          .string()
          .required('Field is required'),
        sproduct_label: yup
          .string()
          .required('Field is required'),
      })
    )
});

const AddSubscriptionModal = ({
  existFrequency,
  isOneTimeEnable,
  openAddingSubscriptionsDialog,
  setOpenAddingSubscriptionsDialog,
  handleSaveSubscriptionOption
}) => {
  const classes = useStyles();

  const {
    editableQuoteProduct
  } = useSelector((state: IReduxState) => state.quote);

  const handleSubmitSubscriptionOptions = (values) => {
    const subscriptionOptions = values.subscriptions.map((item) => {
      const {percent_discount, frequency, ...optionData} = item;
      return optionData;
    });
    handleSaveSubscriptionOption(subscriptionOptions);
  };

  const handleFrequencyChange = (event, index, setFieldValue, handleChange) => {
    const { value } = event.target;
    const [frequencyCount, frequencyUnit] = value.split(/\s+/);
    const ssortOrder = calculateSsortOrder(frequencyCount, frequencyUnit);

    setFieldValue(`subscriptions[${index}].ssort_order`, ssortOrder);
    setFieldValue(`subscriptions[${index}].frequency_unit`, frequencyUnit.replace('s', ''));
    setFieldValue(`subscriptions[${index}].frequency_count`, frequencyCount);
    handleChange(event);
  };

  return (
    <Dialog
      className={classes.dialog}
      open={openAddingSubscriptionsDialog}
      onClose={() => setOpenAddingSubscriptionsDialog(false)}
      aria-labelledby="add-subscription-option"
    >
      <DialogTitle id="add-subscription-option">Subscription Options:</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Choose options for subscription
        </DialogContentText>
        <Formik
          initialValues={{
            subscriptions: [
              {
                product_id: editableQuoteProduct?.id,
                frequency_count: '',
                frequency_unit: '',
                sproduct_label: '',
                installment_price: '',
                additional_information: '',
                ssort_order: '',
                frequency: '',
                percent_discount: ''
              }
            ]
          }}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleSubmitSubscriptionOptions}
        >
          {({
            values,
            errors,
            isValid,
            dirty,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
            setFieldTouched
          }) => (
            <form onSubmit={handleSubmit}>
              <FieldArray name="subscriptions">
                {({push, remove}) => {
                  const listOfUsedOptions = [
                    ...existFrequency.filter((option) => !option.includes('0 null')), //Only one time subscription have frequency '0 null'
                    ...values.subscriptions.map((option) => option.frequency)];
                  const isSelectTextfield = (!isOneTimeEnable && frequencyOptions.length !== existFrequency.length) ||
                                            (isOneTimeEnable && frequencyOptions.length !== (existFrequency.length - 1));
                  const isDisabledTextfield = (!isOneTimeEnable && frequencyOptions.length === existFrequency.length) ||
                                              (isOneTimeEnable && frequencyOptions.length === (existFrequency.length - 1));
                  return (
                    <>
                      <Grid item xs={12}>
                        {values.subscriptions.map((_, index) => (
                          <>
                            <CardContent key={`option-${index}`} className="px-0 flex items-center justify-around">
                              <TextField
                                className="mx-2 min-w-160"
                                name={`subscriptions[${index}].frequency`}
                                select={isSelectTextfield}
                                disabled={isDisabledTextfield}
                                label="Frequency"
                                value={values.subscriptions[index].frequency}
                                variant="outlined"
                                onChange={(event) => handleFrequencyChange(event, index, setFieldValue, handleChange)}
                                onBlur={handleBlur}
                                inputProps={{ className: 'text-center' }}
                                error={errors.subscriptions?.length && Boolean(errors.subscriptions[index]?.frequency)}
                                helperText={errors.subscriptions?.length && errors.subscriptions[index]?.frequency}
                              >
                                {frequencyOptions.map((option, idx) => (
                                  <MenuItem
                                    key={`option-${idx}`}
                                    value={option}
                                    className={`${listOfUsedOptions.includes(option) ? 'hidden' : ''}`}
                                  >
                                    {option}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <TextField
                                className="mx-2 min-w-160"
                                name={`subscriptions[${index}].percent_discount`}
                                select
                                label="Percent discount"
                                variant="outlined"
                                value={values.subscriptions[index].percent_discount}
                                onChange={(event) => {
                                  const {value} = event.target;

                                  if (value) {
                                    setFieldValue(
                                      `subscriptions[${index}].installment_price`,
                                      (editableQuoteProduct?.price - ((editableQuoteProduct?.price / 100) * Number(value.slice(0, -1))))
                                    );
                                    setFieldValue(`subscriptions[${index}].additional_information`, [{discount_percentage: parseFloat(value)}]);
                                  }

                                  handleChange(event);
                                }}
                                onBlur={() => setFieldTouched(`subscriptions[${index}].percent_discount`, true)}
                                inputProps={{ className: 'text-center' }}
                                error={errors.subscriptions?.length && Boolean((errors.subscriptions[index] as unknown as ISubscriptionOption)?.percent_discount)}
                                helperText={errors.subscriptions?.length && (errors.subscriptions[index] as unknown as ISubscriptionOption)?.percent_discount}
                              >
                                {discount.map((option, idx) => (
                                  <MenuItem key={`option-${idx}`} value={option}>
                                    {option}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <TextField
                                required
                                className="mx-2"
                                name={`subscriptions[${index}].sproduct_label`}
                                label={'Option Label'}
                                value={values.subscriptions[index].sproduct_label}
                                variant="outlined"

                                onChange={handleChange}
                                onBlur={handleBlur}
                                inputProps={{ className: 'text-center' }}
                                error={errors.subscriptions?.length && Boolean((errors.subscriptions[index] as unknown as ISubscriptionOption)?.sproduct_label)}
                                helperText={errors.subscriptions?.length && (errors.subscriptions[index] as unknown as ISubscriptionOption)?.sproduct_label}
                              />
                              {values.subscriptions.length > 1 &&
                              <div>
                                <IconButton
                                  onClick={() => remove(index)}
                                  title="Delete"
                                >
                                  <Icon>delete</Icon>
                                </IconButton>
                              </div>
                              }
                            </CardContent>
                            <Grid item xs={12}>
                              {(values.subscriptions.length !== (index + 1)) && <Divider className="my-5" />}
                            </Grid>
                          </>
                        ))}
                        <DialogActions className="m-4">
                          <Button
                            variant="outlined"
                            color="primary"
                            disabled={!(isValid && dirty) || listOfUsedOptions.length === frequencyOptions.length}
                            onClick={() => push(emptySubscription(editableQuoteProduct?.id))}
                          >
                            <span className="capitalize">Add more subscriptions</span>
                          </Button>
                          <Button
                            className="text-muted"
                            onClick={() => setOpenAddingSubscriptionsDialog(false)}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            disabled={!(isValid && dirty)}
                            color="primary"
                            variant="contained"
                          >
                            Create Option
                          </Button>
                        </DialogActions>
                      </Grid>
                    </>
                  );
                }}
              </FieldArray>
            </form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default AddSubscriptionModal;
